/**
 * @file HybridModelText2SpeechProcessor.ts
 *
 * Concrete TTS processor used by `HybridModelSessionContext` for dual-quality (hybrid)
 * recognition sessions. Extends `BaseSpeechProcessor`.
 *
 * Key distinction from `Text2SpeechProcessor`:
 *   The queue stores Azure result **offset IDs** (strings), not raw text. Actual speech
 *   content is resolved lazily at dequeue time via `PhrasesRegistry`, enabling the
 *   processor to upgrade an already-queued unstable (partial) phrase to its stable
 *   (finalized) version before speaking it.
 *
 * Responsibilities:
 * - `enqueueText(resultId, text, isStable)` — calls `PhrasesRegistry.upsertPhrase()` to
 *   insert or update the phrase entry. The result ID is pushed onto the queue only on the
 *   first call (when the phrase is new), preventing duplicate queue entries for subsequent
 *   stable updates to the same recognition result.
 * - `PhrasesRegistry` / `RecognizedPhrase` — registry keyed by Azure result offset ID.
 *   Each `RecognizedPhrase` holds the stable text (if finalized), an array of unstable
 *   fragment snapshots, and a mutable `state` object tracking whether the phrase is
 *   currently being spoken and which unstable fragment index was last read.
 * - `dequeueAsync()` — peeks at the front of the queue and selects what to speak:
 *     - If a stable phrase is already available, dequeues immediately and returns it.
 *     - If only unstable fragments exist, sets `isCurrentlySpeaking = true` and waits
 *       5 seconds (grace period) to give the recognizer time to emit a stable result.
 *       After the timeout, `solveUseStableOrUnstable()` picks stable if it has arrived,
 *       otherwise falls back to the latest unstable fragment.
 * - `solveUseStableOrUnstable(phrase, id)` — calls `RecognizedPhrase.curatePhrase()` to
 *   strip the portion of the text that was already spoken from a previous unstable
 *   fragment, ensuring continuity without repetition.
 * - `start()` — 100 ms async polling interval. Guards with an `isDequeing` boolean flag
 *   to prevent concurrent async dequeue operations. Resolves text via `dequeueAsync()`
 *   then calls the private `speak(text, voiceIndex)`.
 * - `speak(text, voiceIndex)` — builds SSML via `buildSsml()` and sends it to the Azure
 *   `SpeechSynthesizer`; audio is forwarded to `AudioPlayer.play()`.
 * - In `textOnly` mode, `start()` returns immediately and `speak()` discards audio output.
 *
 * Pipeline position:
 *   HybridModelSessionContext
 *     → recognizing event → enqueueText(id, text, isStable=false)
 *     → recognized event  → enqueueText(id, text, isStable=true)
 *       → 100 ms polling → dequeueAsync() → speak() → Azure SpeechSynthesizer → AudioPlayer
 */
import {Output} from "../contexts/Configuration";
import {RecognizedPhrase} from "../contexts/RecognizedPhrase";
import PhrasesRegistry from "../contexts/PhrasesRegistry";
import {BaseSpeechProcessor} from "./SpeechProcessor";


export class HybridModelText2SpeechProcessor extends BaseSpeechProcessor {

    //State
    private phrasesRegistry: PhrasesRegistry = new PhrasesRegistry();
    private isDequeing = false;

    /**
     * Add text to the processor queue.
     * A poller eventually will take this text and perform the following tasks:
     * - Add ssml to the text
     * - Send it to the Text2Speech and get a Speech audio back
     * - Enqueue in the Audio Output the audio
     * @param resultId
     * @param textToSpeak
     * @param isStable
     */
    public enqueueText = (resultId: string, textToSpeak: string, isStable: boolean): void => {
        console.log('[HybridMode_Text2SpeechProcessor] Queueing T2S services...', textToSpeak, resultId, isStable)

        if (this.phrasesRegistry.upsertPhrase(resultId, textToSpeak, isStable)) {
            this.pendingText2SpeechQueue.enqueue(resultId);
        }
    }

    public async dequeueAsync(): Promise<string> {
        console.log('[Dequeue Async] isDequeing?', this.isDequeing)
        const phraseId = this.pendingText2SpeechQueue.peek();
        if (!phraseId) return ''; //Nothing to say / There are no phrases

        const phrase = this.phrasesRegistry.getPhrase(phraseId);
        if (!phrase) return ''; //Nothing to say / There are no phrases

        let nextPhraseToSay = '';

        if (!phrase.state.isCurrentlySpeaking) {
            console.log('[Dequeue Async] Currently, not speaking')
            if (!!phrase.stablePhrase) { //Prioritize speaking stable phrases
                console.log('[Dequeue Async] Returning stable phrase')
                this.pendingText2SpeechQueue.dequeue();
                nextPhraseToSay = phrase.stablePhrase; //nothing to curate
            } else {
                console.log('[Dequeue Async] Will wait 5 secs until stable')
                phrase.state.isCurrentlySpeaking = true;
                return new Promise<string>((resolve, reject) => {
                    setTimeout(() => {
                        nextPhraseToSay = this.solveUseStableOrUnstable(phrase, phraseId);
                        console.log('[Dequeue Async] Returning phrase after 5 secs')
                        resolve(nextPhraseToSay);
                    }, 5000);
                });
            }
        } else {
            console.log('[Dequeue Async] Already speaking')
            phrase.state.isCurrentlySpeaking = true;
            nextPhraseToSay = this.solveUseStableOrUnstable(phrase, phraseId);
        }

        return nextPhraseToSay;
    }

    private solveUseStableOrUnstable(phrase: RecognizedPhrase, id: string) {

        console.log('[Dequeue Async] solveUseStableOrUnstable ')
        let curatedNextPhraseToSay = '';

        if (!!phrase.stablePhrase) { //Prioritize speaking stable phrases
            this.pendingText2SpeechQueue.dequeueObject(id);
            const nextPhraseToSay = phrase.stablePhrase;
            const phraseAlreadySaid = phrase.unstablePhraseFragments?.length > 0 ? phrase.unstablePhraseFragments[phrase.state.unstableFragmentAlreadySaidIndex] : '';
            curatedNextPhraseToSay = RecognizedPhrase.curatePhrase(nextPhraseToSay, phraseAlreadySaid);
            console.log('[Dequeue Async] solveUseStableOrUnstable; returning stable')
        } else { //If there is no stable, use last unstable available
            const phraseAlreadySaid = phrase.unstablePhraseFragments[phrase.state.unstableFragmentAlreadySaidIndex];
            const nextFragmentIndex = phrase.unstablePhraseFragments.length - 1;
            const nextPhraseToSay = phrase.unstablePhraseFragments[nextFragmentIndex] ?? '';
            curatedNextPhraseToSay = RecognizedPhrase.curatePhrase(nextPhraseToSay, phraseAlreadySaid);
            phrase.state.unstableFragmentAlreadySaidIndex = nextFragmentIndex;
            console.log('[Dequeue Async] solveUseStableOrUnstable; returning unstable')
        }
        return curatedNextPhraseToSay;
    }

    /**
     *
     */
    public start(): void {
        if (this.outputConfig.textOnly)
            return;

        if (this.pollingInterval) {
            throw new Error("[Text2SpeechProcessor] Cant start Text2Speech processor. There's already a polling interval running.");
        }

        this.pollingInterval = setInterval(async () => {
            if (this.audioPlayer?.playing() || this.isDequeing)
                return;

            // console.log('[Text2SpeechProcessor] dequeing... Queue', this.pendingText2SpeechQueue);

            this.isDequeing = true;
            console.log('Before dequeue ', this.pendingText2SpeechQueue.items);
            let textToSpeak = await this.dequeueAsync();
            console.log('After dequeue ', this.pendingText2SpeechQueue.items);
            this.isDequeing = false;
            console.log('[Text2SpeechProcessor] dequeued', textToSpeak, this.isDequeing);
            if (!!textToSpeak)
                this.speak(textToSpeak, this.outputConfig.voiceIndex);

        }, 100)
    }

    public getOutputConfig(): Output {
        return this.outputConfig;
    }

    private speak(textToSpeak: string, voiceIndex: number) {

        const sendToDevice = (data: ArrayBuffer) => {
            if (this.outputConfig.textOnly)
                return;
            this.audioPlayer?.play(data)
        }

        const ssml = this.buildSsml(textToSpeak, this.outputConfig.language.speakKey, this.outputConfig.language.voices[voiceIndex].name, this.outputConfig.pace);

        this.speechSynthesizer.speakSsmlAsync(ssml, (speechResult => {
            console.log('[HybridMode_Text2SpeechProcessor] Speech Ready', speechResult, speechResult.audioData);
            sendToDevice(speechResult.audioData);
        }), err => {
            console.log('[HybridMode_Text2SpeechProcessor] Speech error', err);
        });
    }

}
